import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import { products, productCategories } from '../mockData';
import { ShoppingCart, Star, Check, Sparkles, ArrowLeft, Package, Shield, Truck } from 'lucide-react';
import { toast } from 'sonner';

export const ProductDetail = () => {
  const { id } = useParams();
  const product = products.find(p => p.id === parseInt(id));
  const [quantity, setQuantity] = useState(1);

  if (!product) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-4">Product not found</h2>
          <Link to="/shop">
            <Button>Back to Shop</Button>
          </Link>
        </div>
      </div>
    );
  }

  const category = productCategories.find(c => c.id === product.category);
  const relatedProducts = products.filter(p => p.category === product.category && p.id !== product.id).slice(0, 4);

  const addToCart = () => {
    toast.success('Added to cart!', {
      description: `${quantity}x ${product.name} - $${(product.price * quantity).toFixed(2)}`,
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      {/* Back button */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <Link to="/shop">
            <Button variant="ghost" className="gap-2">
              <ArrowLeft className="w-4 h-4" />
              Back to Shop
            </Button>
          </Link>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-16">
          {/* Image */}
          <div>
            <div className="sticky top-24">
              <div className="relative aspect-square rounded-2xl overflow-hidden bg-gray-100 shadow-2xl">
                <img
                  src={product.image}
                  alt={product.name}
                  className="w-full h-full object-cover"
                />
                {product.featured && (
                  <Badge className="absolute top-4 right-4 bg-gradient-to-r from-yellow-600 to-yellow-500 text-black font-semibold border-0 shadow-lg">
                    <Sparkles className="w-4 h-4 mr-1" />
                    Featured
                  </Badge>
                )}
                {product.originalPrice && product.originalPrice > product.price && (
                  <Badge className="absolute top-4 left-4 bg-red-600 text-white font-bold border-0 shadow-lg text-lg px-4 py-2">
                    SAVE {Math.round(((product.originalPrice - product.price) / product.originalPrice) * 100)}%
                  </Badge>
                )}
              </div>

              {/* Trust badges */}
              <div className="grid grid-cols-3 gap-4 mt-6">
                <div className="text-center p-4 bg-white rounded-xl shadow-md">
                  <Shield className="w-6 h-6 text-yellow-600 mx-auto mb-2" />
                  <p className="text-xs font-medium">Guaranteed Authentic</p>
                </div>
                <div className="text-center p-4 bg-white rounded-xl shadow-md">
                  <Truck className="w-6 h-6 text-yellow-600 mx-auto mb-2" />
                  <p className="text-xs font-medium">Free Shipping</p>
                </div>
                <div className="text-center p-4 bg-white rounded-xl shadow-md">
                  <Package className="w-6 h-6 text-yellow-600 mx-auto mb-2" />
                  <p className="text-xs font-medium">Gift Packaging</p>
                </div>
              </div>
            </div>
          </div>

          {/* Details */}
          <div>
            <Badge variant="outline" className="mb-4 border-yellow-600 text-yellow-700 font-medium">
              {category?.name}
            </Badge>

            <h1 className="text-4xl font-black text-gray-900 mb-4 leading-tight">
              {product.name}
            </h1>

            {/* Rating */}
            {product.rating && (
              <div className="flex items-center gap-3 mb-6">
                <div className="flex items-center gap-1">
                  {[...Array(5)].map((_, i) => (
                    <Star
                      key={i}
                      className={`w-5 h-5 ${
                        i < Math.floor(product.rating)
                          ? 'fill-yellow-400 text-yellow-400'
                          : 'text-gray-300'
                      }`}
                    />
                  ))}
                </div>
                <span className="text-lg font-semibold">{product.rating}</span>
                <span className="text-gray-500">({product.reviews} reviews)</span>
              </div>
            )}

            {/* Price */}
            <div className="mb-8">
              <div className="flex items-baseline gap-4 mb-2">
                <span className="text-5xl font-black text-gray-900">
                  ${product.price}
                </span>
                {product.originalPrice && product.originalPrice > product.price && (
                  <span className="text-2xl text-gray-400 line-through">
                    ${product.originalPrice}
                  </span>
                )}
              </div>
              <p className="text-sm text-gray-600">Tax included. Free shipping on orders over $100</p>
            </div>

            {/* Description */}
            <div className="prose prose-sm mb-8">
              <p className="text-gray-700 text-lg leading-relaxed">
                {product.longDescription || product.description}
              </p>
            </div>

            {/* Quantity & Add to Cart */}
            <div className="bg-gradient-to-br from-yellow-50 to-yellow-100/50 rounded-2xl p-6 mb-8 border-2 border-yellow-200">
              <div className="flex items-center gap-4 mb-4">
                <label className="text-sm font-semibold text-gray-700">Quantity:</label>
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                    className="w-10 h-10"
                  >
                    -
                  </Button>
                  <span className="text-xl font-bold w-12 text-center">{quantity}</span>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setQuantity(quantity + 1)}
                    className="w-10 h-10"
                  >
                    +
                  </Button>
                </div>
              </div>

              <Button
                size="lg"
                onClick={addToCart}
                disabled={!product.inStock}
                className="w-full bg-gradient-to-r from-yellow-600 via-yellow-500 to-yellow-400 hover:from-yellow-500 hover:via-yellow-400 hover:to-yellow-300 text-black font-bold text-lg py-6 shadow-xl"
              >
                <ShoppingCart className="w-6 h-6 mr-2" />
                {product.inStock ? `Add to Cart - $${(product.price * quantity).toFixed(2)}` : 'Out of Stock'}
              </Button>
            </div>

            {/* Features */}
            {product.features && product.features.length > 0 && (
              <Card className="mb-8 border-0 shadow-lg">
                <CardContent className="p-6">
                  <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
                    <Sparkles className="w-5 h-5 text-yellow-600" />
                    Key Features
                  </h3>
                  <ul className="space-y-3">
                    {product.features.map((feature, index) => (
                      <li key={index} className="flex items-start gap-3">
                        <Check className="w-5 h-5 text-yellow-600 mt-0.5 flex-shrink-0" />
                        <span className="text-gray-700">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            )}

            {/* Specifications */}
            {product.specifications && product.specifications.length > 0 && (
              <Card className="border-0 shadow-lg">
                <CardContent className="p-6">
                  <h3 className="text-xl font-bold mb-4">Technical Specifications</h3>
                  <ul className="space-y-2">
                    {product.specifications.map((spec, index) => (
                      <li key={index} className="text-sm text-gray-700 py-2 border-b border-gray-100 last:border-0">
                        {spec}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            )}
          </div>
        </div>

        {/* Related Products */}
        {relatedProducts.length > 0 && (
          <div className="mt-16">
            <h2 className="text-3xl font-black text-gray-900 mb-8">You May Also Like</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {relatedProducts.map((relatedProduct) => (
                <Link key={relatedProduct.id} to={`/shop/${relatedProduct.id}`}>
                  <Card className="group overflow-hidden border-0 shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:scale-105">
                    <div className="relative h-48 overflow-hidden">
                      <img
                        src={relatedProduct.image}
                        alt={relatedProduct.name}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                      />
                    </div>
                    <CardContent className="p-4">
                      <h3 className="font-bold text-sm mb-2 line-clamp-2">{relatedProduct.name}</h3>
                      <div className="flex items-center justify-between">
                        <span className="text-xl font-black">${relatedProduct.price}</span>
                        {relatedProduct.rating && (
                          <div className="flex items-center gap-1">
                            <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                            <span className="text-sm font-semibold">{relatedProduct.rating}</span>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};